import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { SeatsioDesigner } from '@seatsio/seatsio-react';
import {
    ArrowLeft, CheckCircle, Maximize2, Minimize2, Loader2
} from 'lucide-react';


const DesignerContainer = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Fullscreen styles applied via browser's native fullscreen API */
  &:fullscreen {
    height: 100vh;
    width: 100vw;
    border-radius: 0;
  }
  
  &:-webkit-full-screen {
    height: 100vh;
    width: 100vw;
    border-radius: 0;
  }
  
  &:-moz-full-screen {
    height: 100vh;
    width: 100vw;
    border-radius: 0;
  }
  
  &:-ms-fullscreen {
    height: 100vh;
    width: 100vw;
    border-radius: 0;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const ToolbarTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: #333;
`;

const ToolbarActions = styled.div`
display: flex;
gap: 0.5rem;
`;

const DesignerWrapper = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  background: white;

`;

const ControlButton = styled.button`
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #24292e;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  
  &:focus {
    outline: none;
  }
  
  &:hover {
    background: #f8f9fa;
    border-color: #d1d5db;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
    color: #000;
  }

  &:active {
    transform: translateY(0);
    background: #f1f3f5;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 16px;
    height: 16px;
    color: #6b7280;
  }

  &:hover svg {
    color: #111827;
  }
`;

// ... (keep LoadingOverlay, InfoMessage, ErrorMessage, ActionButtons, ActionButton styles)
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  
  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const InfoMessage = styled.div`
background: #d4edda;
color: #155724;
padding: 0.75rem 1rem;
border - radius: 6px;
font - size: 0.9rem;
margin: 0;
border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
background: #f8d7da;
color: #721c24;
padding: 1rem;
border - radius: 8px;
margin: 1rem;
border: 1px solid #f5c6cb;
`;



const ValidationStatus = styled.div`
  background: ${props => props.isValid ? '#d4edda' : props.isValidating ? '#fff3cd' : '#f8d7da'};
  color: ${props => props.isValid ? '#155724' : props.isValidating ? '#856404' : '#721c24'};
  border: 1px solid ${props => props.isValid ? '#c3e6cb' : props.isValidating ? '#ffeaa7' : '#f5c6cb'};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Error Boundary to catch Seats.io internal errors
class ChartErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Specifically ignore the double-destroy error as it's harmless during unmount
        if (error.message && error.message.includes('Cannot destroy a chart that has already been destroyed')) {
            console.warn('Caught expected Seats.io destruction error:', error);
            return;
        }
        console.error('SeatsioDesigner error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // Return null or specific error UI to prevent crash
            // For destruction errors during navigation, rendering null is often preferred
            return null;
        }
        return this.props.children;
    }
}

// Widget Component
const SeatsioDesignerWidget = ({
    chartKey,
    eventTitle,
    onDesignComplete,
    onCancel
}) => {
    // ... (keep state hooks)
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [isDestroyed] = useState(false);
    const [designerConfig, setDesignerConfig] = useState(null);
    const containerRef = React.useRef(null);

    // ... (keep fetchDesignerConfig, useEffects, handleCancel, toggleFullscreen, validateChart, handleDesignComplete)

    // Fetch designer configuration securely from backend
    const fetchDesignerConfig = React.useCallback(async () => {
        if (!chartKey) {
            setError('Chart key is required for design');
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/seatsio/designer-config/${chartKey}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to get designer config: ${response.status} `);
            }

            const result = await response.json();

            if (result.success) {
                setDesignerConfig({
                    secretKey: result.data.secretKey,
                    chartKey: chartKey,
                    region: result.data.region,
                    language: 'en'
                });
                setError(null);
            } else {
                throw new Error(result.message || 'Failed to get designer configuration');
            }

        } catch (error) {
            console.error('Error fetching designer config:', error);
            setError('Failed to load designer configuration: ' + error.message);
            setIsLoading(false);
        }
    }, [chartKey]);

    // Validate props and fetch config
    useEffect(() => {
        fetchDesignerConfig();
    }, [fetchDesignerConfig]);

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const toggleFullscreen = async () => {
        try {
            if (!isFullscreen) {
                // Enter fullscreen using browser's native fullscreen API
                const element = containerRef.current;
                if (element) {
                    if (element.requestFullscreen) {
                        await element.requestFullscreen();
                    } else if (element.webkitRequestFullscreen) {
                        await element.webkitRequestFullscreen();
                    } else if (element.msRequestFullscreen) {
                        await element.msRequestFullscreen();
                    }
                }
            } else {
                // Exit fullscreen
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }
            }
        } catch (error) {
            console.error('Fullscreen toggle failed:', error);
        }
    };

    const validateChart = async () => {
        if (!chartKey) return;

        try {
            setIsValidating(true);
            setValidationResult(null);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/seatsio/validate-chart/${chartKey}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Validation failed: ${response.status} `);
            }

            const result = await response.json();

            if (result.success) {
                setValidationResult({
                    isValid: result.data.isValid,
                    message: result.data.message
                });
            } else {
                throw new Error(result.message || 'Validation failed');
            }

        } catch (error) {
            console.error('Error validating chart:', error);
            setValidationResult({
                isValid: false,
                message: 'Error validating chart: ' + error.message
            });
        } finally {
            setIsValidating(false);
        }
    };

    // Handle fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        // Listen for fullscreen change events
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Manual cleanup of designerInstance is not needed as the SeatsioDesigner component 
    // from @seatsio/seatsio-react handles destruction in its own componentWillUnmount.
    // Double destruction causes the "Cannot destroy a chart that has already been destroyed" error.




    if (error) {
        return (
            <DesignerContainer style={{ height: 'auto', padding: '2rem' }}>
                <ToolbarTitle>Chart Designer</ToolbarTitle>
                <ErrorMessage>{error}</ErrorMessage>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                    <ControlButton onClick={handleCancel}>
                        Continue Without Design
                    </ControlButton>
                </div>
            </DesignerContainer>
        );
    }

    if (!chartKey || !designerConfig) {
        return (
            <DesignerContainer style={{ height: 'auto', padding: '2rem' }}>
                <ToolbarTitle>Chart Designer</ToolbarTitle>
                <InfoMessage style={{ marginTop: '1rem' }}>
                    {!chartKey ? 'Chart is being created. Please wait...' : 'Loading designer configuration...'}
                </InfoMessage>
            </DesignerContainer>
        );
    }

    return (
        <DesignerContainer ref={containerRef}>
            <Toolbar>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <ControlButton onClick={handleCancel} title="Back to Maps" style={{ border: 'none', background: 'transparent', boxShadow: 'none', paddingLeft: 0 }}>
                        <ArrowLeft />
                        <span style={{ fontSize: '1rem' }}>Back</span>
                    </ControlButton>
                    <div style={{ width: 1, height: '24px', background: '#e5e7eb', margin: '0 0.5rem' }}></div>
                    <ToolbarTitle>{eventTitle || 'Design Seating Chart'}</ToolbarTitle>
                </div>
                <ToolbarActions>
                    {validationResult && (
                        <ValidationStatus
                            isValid={validationResult.isValid}
                            isValidating={isValidating}
                        >
                            {validationResult.isValid ? '✅ Chart Valid' : '❌ Issues Found'}
                        </ValidationStatus>
                    )}

                    <ControlButton
                        onClick={validateChart}
                        disabled={isValidating}
                        title="Validate chart configuration"
                        style={{
                            borderColor: isValidating ? '#e5e7eb' : '#3b82f6',
                            color: isValidating ? '#9ca3af' : '#2563eb',
                            background: isValidating ? '#f9fafb' : '#eff6ff'
                        }}
                    >
                        {isValidating ? <Loader2 className="animate-spin" /> : <CheckCircle style={{ color: '#2563eb' }} />}
                        {isValidating ? 'Checking...' : 'Validate'}
                    </ControlButton>

                    <ControlButton onClick={toggleFullscreen} title="Toggle Fullscreen">
                        {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                        {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                    </ControlButton>
                </ToolbarActions>
            </Toolbar>

            <DesignerWrapper data-designer-wrapper>
                {isLoading && (
                    <LoadingOverlay>
                        <div className="spinner" />
                    </LoadingOverlay>
                )}

                <ChartErrorBoundary>
                    <SeatsioDesigner
                        {...designerConfig}
                        onRenderStarted={(designer) => {
                            console.log('Seats.io designer render started');
                        }}
                        onDesignerRendered={(designer) => {
                            console.log('Seats.io designer rendered successfully');
                            if (!isDestroyed) {
                                setIsLoading(false);
                                setError(null);
                            }
                        }}
                        onDesignerRenderingFailed={(error) => {
                            console.error('Seats.io designer rendering failed:', error);
                            if (!isDestroyed) {
                                setError(`Failed to load chart designer: ${error?.message || error}.`);
                                setIsLoading(false);
                            }
                        }}
                    />
                </ChartErrorBoundary>
            </DesignerWrapper>

        </DesignerContainer>
    );
};

export default SeatsioDesignerWidget;
